import { Component, inject, OnInit } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions, ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule
} from 'ng-apexcharts';
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { DashboardService } from "../../services/dashboard/dashboard.service";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  title: ApexTitleSubtitle;
  tooltip: ApexTooltip;
};

@Component({
  selector: 'api-calls',
  standalone: true,
  imports: [NgApexchartsModule, CommonModule, NzSkeletonModule, NzButtonModule, FormsModule, NzDatePickerModule],
  templateUrl: './api-calls.component.html',
  styleUrl: './api-calls.component.scss'
})
export class ApiCallsComponent implements OnInit {
  dashboardService = inject(DashboardService);

  public chartOptions : ChartOptions | undefined;
  public chartType: 'bar' | 'heatmap' = 'heatmap';
  public dateRange: Date[] = [];

  private apiCallsRecords: any[] = [];
  private allCategories: string[] = [];
  private apiCallsSeries: ApexAxisChartSeries = [];
  private apiCallsCategories: string[] = [];

  ngOnInit(): void {
    this.dashboardService.getDataApiCallsStatistics().subscribe(res => {

      this.apiCallsRecords = [...res].sort((a: any, b: any) =>
        a.customer_name.localeCompare(b.customer_name) ||
        a.api_key_name.localeCompare(b.api_key_name) ||
        a.endpoint.localeCompare(b.endpoint)
      );
      this.allCategories = res[0]?.categories ?? [];
      this.dateRange = this.allCategories.length
        ? [new Date(this.allCategories[0]), new Date(this.allCategories[this.allCategories.length - 1])]
        : [];

      this.applyDateFilter();
    })

  }

  setChartType(type: 'bar' | 'heatmap') {
    this.chartType = type;
    this.buildChart();
  }

  onDateRangeChange(range: Date[]): void {
    this.dateRange = range ?? [];
    this.applyDateFilter();
  }

  private applyDateFilter(): void {
    const [start, end] = this.dateRange;
    const indices = this.allCategories
      .map((category, index) => ({ category, index }))
      .filter(({ category }) =>
        (!start || new Date(category) >= start) && (!end || new Date(category) <= end)
      )
      .map(({ index }) => index);

    this.apiCallsCategories = indices.map(i => this.allCategories[i]);
    this.apiCallsSeries = this.apiCallsRecords.map((entry: any) => ({
      name: `${entry.customer_name} — ${entry.api_key_name} — ${entry.endpoint}`,
      data: indices.map(i => entry.data[i])
    }));

    this.buildChart();
  }

  private buildChart(): void {
    this.chartOptions = {
      series: this.apiCallsSeries,
      chart: {
        type: this.chartType,
        height: this.chartType === 'heatmap' ? Math.max(400, this.apiCallsSeries.length * 44) : 400,
        stacked: this.chartType === 'bar'
      },
      title: {
        text: ''
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false
        },
        heatmap: {
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: '#ECEFF1', name: '0' },
              { from: 1, to: 50, color: '#BBDEFB', name: '1-50' },
              { from: 51, to: 200, color: '#64B5F6', name: '51-200' },
              { from: 201, to: 1000, color: '#1E88E5', name: '201-1000' },
              { from: 1001, to: 5000, color: '#1565C0', name: '1001-5000' },
              { from: 5001, to: 13000, color: '#0D47A1', name: '5001+' }
            ]
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: this.apiCallsCategories,
      },
      yaxis: {
        title: {
          text: ""
        },
        labels: {
          formatter: function (value: any) {
            if (typeof value === 'number') {
              return value.toLocaleString('de-DE');
            }
            const parts = String(value).split(' — ');
            return parts.length > 1 ? parts.slice(0, 2) : [String(value)];
          }
        }
      },
      tooltip: {
        custom: ({series, seriesIndex, dataPointIndex, w}: any) => {
          const [customer, apiKey, endpoint] = (w.globals.seriesNames[seriesIndex] as string).split(' — ');
          const date = w.globals.labels[dataPointIndex];
          const value = series[seriesIndex][dataPointIndex];

          return `
            <div class="api-calls-tooltip">
              <div class="api-calls-tooltip__customer">${customer}</div>
              <div class="api-calls-tooltip__key">${apiKey}</div>
              <div class="api-calls-tooltip__endpoint">${endpoint ?? ''}</div>
              <div class="api-calls-tooltip__meta">${date} — <strong>${Number(value).toLocaleString('de-DE')}</strong> calls</div>
            </div>
          `;
        }
      }
    };
  }
}
